import { Controller } from "@hotwired/stimulus"

/**
 * Drag-to-create / drag-to-move / drag-to-resize on Ui::Calendar grids.
 *
 * Attached only when `Ui::Calendar::WeekGridComponent.new(editable: true)` —
 * Support's read-only views don't get this. T4 (page-level controller) listens
 * for the `ui-calendar:event-changed` CustomEvent we emit and issues a PATCH /
 * POST via Turbo.
 *
 * Markup contract (rendered by T1):
 *
 *   <div data-controller="ui--calendar--editable-grid"
 *        data-ui--calendar--editable-grid-snap-minutes-value="15"
 *        data-ui--calendar--editable-grid-min-minutes-value="15"
 *        data-ui--calendar--editable-grid-hour-height-value="48">
 *     <div data-ui--calendar--editable-grid-target="cell"
 *          data-start-at="2026-05-28T08:00:00Z"></div>
 *     ...
 *     <div data-ui--calendar--editable-grid-target="chip"
 *          data-event-id="42"
 *          data-start-at="..."
 *          data-end-at="..."
 *          style="top: 384px; height: 48px"></div>
 *   </div>
 *
 * Emits CustomEvent `ui-calendar:event-changed` with detail
 *   { id?, start_at, end_at, action: 'create' | 'move' | 'resize' }
 * and, for a Week grid's all-day bar, `all_day: true | false` too.
 *
 * Chips move / resize vertically. With timeZone / firstHour values (the
 * grids pass them, CONEK-8153) the preview and the saved times follow the
 * WALL CLOCK, as the grid draws chips: a drag across a DST change lands where
 * its preview shows (a drop in the spring-forward gap lands just after it).
 * Without them (older markup) the drag adds elapsed minutes.
 *
 * All-day bars (target "bar", Week grid only) move — or resize from their
 * end handle — by whole days, horizontally, the pointer kept to the visible
 * columns. The preview is the bar's real span shifted, clipped to the
 * visible columns: exactly what the drop saves. An all-day bar saves the
 * midnights of its new first day and of the day after its new last day, in
 * its data-time-zone; a timed 24 h+ bar keeps its times of day on the wall
 * clock. Escape or a pointercancel mid-drag cancels it: nothing is emitted,
 * and that Escape stops there. One pointer at a time: while a press is in
 * progress, other non-primary pointers (a second finger) are ignored; a new
 * primary press drops a press whose pointerup was lost.
 *
 * NOTE: Pointer events (not Mouse) — works on touch (iOS/Android).
 */
// Pixels the pointer must travel before a press becomes a drag. Below this a
// press is a *click*: chips navigate to their booking (the <a> href), empty
// cells open the new-booking modal at that time. This is what stops a plain
// click on an event from spawning a stray drag-ghost in the wrong column.
const DRAG_THRESHOLD_PX = 5

export default class extends Controller {
  static targets = ["chip", "cell", "bar"]
  static values = {
    snapMinutes: { type: Number, default: 15 },
    minMinutes: { type: Number, default: 15 },
    hourHeight: { type: Number, default: 48 },
    // A chip's minimum drawn height: the grids pass
    // Ui::Calendar::LaneLayout::MIN_HEIGHT_PX, so a preview is as tall as
    // the chip the grid will draw.
    chipMinPx: { type: Number, default: 20 },
    // The grid's IANA zone and first visible hour: chips are drawn on the
    // wall clock in it (Ui::Calendar::DayColumn). Empty = elapsed-time math.
    timeZone: { type: String, default: "" },
    firstHour: { type: Number, default: 0 },
  }

  connect() {
    this._onPointerDown = this._onPointerDown.bind(this)
    this._onPointerMove = this._onPointerMove.bind(this)
    this._onPointerUp = this._onPointerUp.bind(this)
    this._onPointerCancel = this._onPointerCancel.bind(this)
    this._onKeyDown = this._onKeyDown.bind(this)
    this._onContextMenu = this._onContextMenu.bind(this)
    // pointerId → stop(), one per ignored press awaiting its lift.
    this._liftWatchers = new Map()
    this.element.addEventListener("pointerdown", this._onPointerDown)
    this._pending = null
    this._drag = null
    this._swallowRelease = null
    this._activePointer = null
    this._activePointerType = null
    this._draggingEl = null
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this._onPointerDown)
    this._reset()
    this._liftWatchers.forEach((stop) => stop())
  }

  // Drops any press / drag in progress: listeners off, ghost gone, nothing
  // emitted.
  _reset() {
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("pointerup", this._onPointerUp)
    window.removeEventListener("pointercancel", this._onPointerCancel)
    window.removeEventListener("keydown", this._onKeyDown, true)
    window.removeEventListener("contextmenu", this._onContextMenu, true)
    this._cleanupGhost()
    this._clearDragging()
    this._drag = null
    this._pending = null
    this._swallowRelease = null
    this._activePointer = null
    this._activePointerType = null
  }

  // The chip / bar being dragged, and the grid root, carry data-dragging
  // while it is — "move", "resize-y" (a time-grid chip) or "resize-x" (a
  // bar): the grids style the root, and everything inside it, with that
  // drag's cursor (grabbing, ns-resize, ew-resize), so the cursor holds
  // wherever the pointer goes.
  // A create drag has no element of its own: only the root is marked.
  _markDragging(el, kind) {
    this._clearDragging()
    if (el) el.dataset.dragging = kind
    this.element.dataset.dragging = kind
    this._draggingEl = el
  }

  _clearDragging() {
    if (this._draggingEl) delete this._draggingEl.dataset.dragging
    delete this.element.dataset.dragging
    this._draggingEl = null
  }

  // While a chip / bar is pressed, a long press must not open the context
  // menu (Android Chrome, after ~500 ms), which would cancel the pointer.
  _onContextMenu(event) {
    event.preventDefault()
  }

  // The click a press produces fires when its finger lifts (held up to
  // 30 s): arm the suppression on that lift (pointerup / pointercancel
  // of that pointer), not at its press. The watcher lasts until that finger
  // lifts (at most 30 s), or until a new press reuses the pointer id (its
  // lift was lost: dropped before that press is handled), or disconnect.
  _suppressClickOnLift(el, pointerId) {
    const previous = this._liftWatchers.get(pointerId)
    if (previous) previous()
    const backstop = setTimeout(() => stop(), 30_000)
    const stop = () => {
      clearTimeout(backstop)
      window.removeEventListener("pointerup", onLift, true)
      window.removeEventListener("pointercancel", onLift, true)
      if (this._liftWatchers.get(pointerId) === stop) this._liftWatchers.delete(pointerId)
    }
    const onLift = (event) => {
      if (event.pointerId !== pointerId) return
      stop()
      this._suppressNextClick(el)
    }
    window.addEventListener("pointerup", onLift, true)
    window.addEventListener("pointercancel", onLift, true)
    this._liftWatchers.set(pointerId, stop)
  }

  // Events from a pointer other than the one that pressed are not ours.
  _otherPointer(event) {
    return this._activePointer !== null && event.pointerId !== this._activePointer
  }

  // ── Pointer handlers ──────────────────────────────────────────────────────

  _onPointerDown(event) {
    if (event.button !== undefined && event.button !== 0) return
    // A Ctrl+click is the Mac's secondary click: it opens the link's context
    // menu, never a press or drag of ours.
    if (event.pointerType === "mouse" && event.ctrlKey) return
    // A press reusing a watched pointer id: that watcher's lift was lost.
    const staleWatcher = this._liftWatchers.get(event.pointerId)
    if (staleWatcher) staleWatcher()

    const target = event.target instanceof Element ? event.target : null
    if (!target) return

    // One pointer at a time. While a press is recorded, another pointer:
    // * of the same type and non-primary (a second finger) is ignored;
    // * of another type is ignored only during a LIVE mouse drag (a touch
    //   tap on a touchscreen laptop mid-drag) — and the click that tap
    //   produces on any link in the grid (a draggable chip / bar or a plain
    //   one) is swallowed when it lifts, so it opens nothing mid-drag;
    // * otherwise (a primary press of the same type, or another type while
    //   the recorded press is only pending, a non-mouse drag, or a release
    //   left to swallow after Escape) means the recorded press never ended:
    //   it is dropped, emitting nothing, and the new press is accepted.
    if (this._pending || this._drag || this._swallowRelease) {
      if (this._otherPointer(event)) {
        const otherType = event.pointerType !== this._activePointerType
        const liveMouseDrag = this._drag !== null && this._activePointerType === "mouse"
        if (otherType ? liveMouseDrag : !event.isPrimary) {
          const link = otherType ? target.closest("a[href]") : null
          if (link && this.element.contains(link)) this._suppressClickOnLift(link, event.pointerId)
          return
        }
      }
      this._reset()
    }

    const handle = target.closest("[data-resize-handle]")
    const bar = target.closest('[data-ui--calendar--editable-grid-target="bar"]')
    const chip = target.closest(
      '[data-ui--calendar--editable-grid-target="chip"]',
    )
    const cell = target.closest(
      '[data-ui--calendar--editable-grid-target="cell"]',
    )

    let mode = null
    let source = null
    if (bar) {
      mode = handle && bar.contains(handle) ? "bar-resize" : "bar-move"
      source = bar
    } else if (handle && chip) {
      mode = "resize"
      source = chip
    } else if (chip && chip.dataset.resizeOnly === "true") {
      // A later day's piece of a multi-day event: only its end can move
      // (from the handle); a press elsewhere is a plain click.
      return
    } else if (chip) {
      mode = "move"
      source = chip
    } else if (cell) {
      mode = "create"
      source = cell
    } else {
      return
    }

    // Record the intent but DON'T start dragging or preventDefault yet — that
    // would swallow the click. The drag only begins once the pointer crosses
    // DRAG_THRESHOLD_PX (see _onPointerMove). Until then the press is a click.
    this._pending = {
      mode,
      source,
      startClientX: event.clientX,
      startClientY: event.clientY,
    }
    this._activePointer = event.pointerId
    this._activePointerType = event.pointerType
    // A touch / pen press whose release is lost (browsers normally send a
    // pointercancel) is dropped by the next press; until then its Escape
    // capture stays armed. A deliberate long hold is never dropped.

    window.addEventListener("pointermove", this._onPointerMove)
    window.addEventListener("pointerup", this._onPointerUp)
    window.addEventListener("pointercancel", this._onPointerCancel)
    // A touch / pen long-press on a chip or bar would open the context menu
    // (Android) and cancel the pointer. A mouse keeps its context menu.
    if (mode !== "create" && (event.pointerType === "touch" || event.pointerType === "pen")) {
      window.addEventListener("contextmenu", this._onContextMenu, true)
    }
    // Capture phase: Escape mid-drag is ours, before a surrounding dialog's.
    window.addEventListener("keydown", this._onKeyDown, true)
  }

  _onPointerMove(event) {
    // A mouse moving with no button down while a mouse press is recorded:
    // that press lost its pointerup (released outside the window, say), so
    // drop it, emitting nothing — checked before the pointer-id filter, as
    // the lost press may have had another id. A hovering mouse never drops
    // a touch or pen press.
    if (event.pointerType === "mouse" && event.buttons === 0 && this._activePointerType === "mouse") {
      this._reset()
      return
    }
    if (this._otherPointer(event)) return
    // Promote a pending press to a real drag once it moves past the threshold.
    if (this._pending && !this._drag) {
      const dx = event.clientX - this._pending.startClientX
      const dy = event.clientY - this._pending.startClientY
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return
      this._beginDrag()
    }

    if (!this._drag) return
    // Now that we're dragging, suppress text-selection / scroll.
    if (event.cancelable) event.preventDefault()
    if (this._drag.mode.startsWith("bar")) {
      this._updateBarGhost(event.clientX)
      return
    }
    const dy = event.clientY - this._drag.startClientY
    this._updateGhost(dy)
  }

  // The browser took the pointer (scroll, OS gesture): no pointerup follows
  // for this press, so drop it — nothing is saved, no click-create later.
  _onPointerCancel(event) {
    if (this._otherPointer(event)) return
    this._reset()
  }

  // Escape during a press or drag cancels it, and goes no further (a
  // surrounding modal stays open). The release that follows must not open
  // the booking either, so it is swallowed (see #_onPointerUp). With no
  // press in progress Escape is left alone (the listener is only on then).
  _onKeyDown(event) {
    if (event.key !== "Escape" || !(this._drag || this._pending)) return
    event.preventDefault()
    event.stopImmediatePropagation()
    const source = this._drag
      ? this._drag.sourceChip
      : this._pending.mode === "create" ? null : this._pending.source
    this._abandon(source)
  }

  // Drops the press / drag in progress, emitting nothing. When it was on a
  // chip or bar, its release is swallowed so it doesn't open the booking.
  _abandon(source) {
    const pointer = this._activePointer
    const pointerType = this._activePointerType
    this._reset()
    if (source) {
      this._swallowRelease = source
      this._activePointer = pointer
      this._activePointerType = pointerType
      window.addEventListener("pointerup", this._onPointerUp)
      window.addEventListener("pointercancel", this._onPointerCancel)
    }
  }

  _onPointerUp(event) {
    if (this._otherPointer(event)) return
    this._activePointer = null
    this._activePointerType = null
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("pointerup", this._onPointerUp)
    window.removeEventListener("pointercancel", this._onPointerCancel)
    window.removeEventListener("keydown", this._onKeyDown, true)
    window.removeEventListener("contextmenu", this._onContextMenu, true)
    this._clearDragging()

    // After any real drag the next click is swallowed, whatever the pointer
    // type: a touch moved less than the browser's tap tolerance still fires
    // one. The suppression lapses after 350 ms, so it only ever eats a very
    // quick re-tap.
    if (this._swallowRelease) {
      this._suppressNextClick(this._swallowRelease)
      this._swallowRelease = null
      return
    }

    // A real drag (chip, bar or create) → finalize it. Whatever happens —
    // nothing changed (dragged out and back, a resize to the same end), or
    // finalize throws — the ghost and state are cleared and, for a chip or
    // bar, the trailing click never opens the booking. The change is emitted
    // only when finalize succeeded; one it can't compute saves nothing.
    if (this._drag) {
      const d = this._drag
      const source = d.mode === "create" ? null : d.sourceChip
      let result = null
      try {
        result = d.mode.startsWith("bar")
          ? this._finalizeBar(event.clientX)
          : this._finalize(event.clientY - d.startClientY)
      } catch (error) {
        result = null
        this._reportError(error)
      } finally {
        this._cleanupGhost()
        this._drag = null
        this._pending = null
        if (source) this._suppressNextClick(source)
      }
      if (result) this._emitChange(result)
      return
    }

    // No drag — it was a click.
    const pending = this._pending
    this._pending = null
    if (!pending) return
    // Click on an empty cell = create a default-length booking at that time
    // (chips fall through to their native <a> navigation).
    if (pending.mode === "create") {
      const result = this._clickCreate(pending.source)
      if (result) this._emitChange(result)
    }
  }

  // ── Drag setup ────────────────────────────────────────────────────────────

  // Turn the recorded _pending press into an active drag (build the ghost).
  _beginDrag() {
    const p = this._pending
    if (!p) return
    // A start that throws (unreadable markup) must not leave a ghost behind
    // or turn the release into a click: the gesture is dropped, nothing
    // emitted, and the release on a chip / bar opens nothing.
    const abandoned = p.mode === "create" ? null : p.source
    try {
      if (p.mode === "create") {
        this._startCreate(p.startClientY, p.source)
      } else if (p.mode === "move") {
        this._startMove(p.startClientY, p.source)
      } else if (p.mode === "resize") {
        this._startResize(p.startClientY, p.source)
      } else if (p.mode.startsWith("bar")) {
        this._startBar(p.mode, p.source, p.startClientX)
      }
      // Markup it can't drag from (a chip without its times, a bar without
      // its all-day row, slot, dates or a zone this browser knows): still a
      // drag, never a click that opens the booking (or creates one) after it.
      if (!this._drag) this._abandon(abandoned)
      else {
        // A create drag extends a time range vertically, as a chip resize.
        const kind = { "bar-resize": "resize-x", resize: "resize-y", create: "resize-y" }[p.mode] || "move"
        this._markDragging(this._drag.sourceChip, kind)
      }
    } catch (error) {
      this._abandon(abandoned)
      this._reportError(error)
    }
  }

  // A drag that threw is cleaned up (see the callers), but never silently:
  // re-thrown on its own task, so it shows in the console and reaches
  // window.onerror / the host's error tracking without breaking the cleanup.
  _reportError(error) {
    setTimeout(() => {
      throw error
    })
  }

  // Click (no drag) on an empty cell → create a min-length booking there.
  _clickCreate(cell) {
    const startAt = cell.dataset.startAt
    if (!validInstant(startAt)) return null
    const start = new Date(startAt)
    const end = new Date(start.getTime() + this.minMinutesValue * 60_000)
    return {
      action: "create",
      start_at: start.toISOString(),
      end_at: end.toISOString(),
    }
  }

  _startCreate(startClientY, cell) {
    const startAt = cell.dataset.startAt
    if (!validInstant(startAt)) return
    const ghost = this._buildGhost(cell)
    const wall = this._wallContext(cell)
    const anchorStart = new Date(startAt)
    // On the wall clock the ghost starts where the chip will be drawn (a
    // spring-forward gap's cell starts after the gap).
    if (wall) ghost.style.top = this._wallPx(anchorStart, wall) + "px"
    this._drag = {
      mode: "create",
      startClientY,
      anchorStart,
      anchorEnd: new Date(anchorStart.getTime() + this.minMinutesValue * 60_000),
      ghost,
      sourceChip: null,
      wall,
    }
  }

  _startMove(startClientY, chip) {
    const startAt = chip.dataset.startAt
    const endAt = chip.dataset.endAt
    if (!validInstant(startAt) || !validInstant(endAt)) return
    const ghost = this._cloneGhostFromChip(chip)
    const anchorStart = new Date(startAt)
    const anchorEnd = new Date(endAt)
    // The ghost previews the WHOLE entry from times, not from the drawn box
    // (which may be clipped at midnight / the visible hours, or nudged up to
    // keep its minimum height): it starts at the entry's real start in this
    // column (data-entry-top-px, wall clock) and is as tall as it lasts.
    const pieceStart = this._pieceStart(chip, anchorStart)
    const baseTop =
      this._pxData(chip, "entryTopPx") ??
      chip.offsetTop - this._minutesToPx((pieceStart - anchorStart) / 60_000)
    // As tall as the entry is drawn (a zero-length one an hour); the move
    // itself keeps the real end. Kept inside the column, as the chip is.
    const baseHeight = Math.max(
      this.chipMinPxValue,
      this._minutesToPx(
        (this._drawnEnd(chip, anchorEnd) - anchorStart) / 60_000,
      ),
    )
    this._placeGhost(ghost, baseTop, baseHeight)
    this._drag = {
      mode: "move",
      startClientY,
      anchorStart,
      anchorEnd,
      drawnEnd: this._drawnEnd(chip, anchorEnd),
      baseTop,
      baseHeight,
      ghost,
      sourceChip: chip,
      eventId: chip.dataset.eventId,
      wall: this._wallContext(chip),
    }
  }

  _startResize(startClientY, chip) {
    const startAt = chip.dataset.startAt
    const endAt = chip.dataset.endAt
    if (!validInstant(startAt) || !validInstant(endAt)) return
    const ghost = this._cloneGhostFromChip(chip)
    const anchorStart = new Date(startAt)
    // From the piece's own top (data-piece-top-px), not the drawn box, which
    // #visible_box may have nudged up near the column's bottom.
    const pieceTop = this._pxData(chip, "pieceTopPx") ?? chip.offsetTop
    ghost.style.top = pieceTop + "px"
    this._drag = {
      mode: "resize",
      pieceTop,
      startClientY,
      anchorStart,
      // From the end the chip is drawn to, where its handle sits (a
      // zero-length entry is drawn an hour long), in both directions.
      anchorEnd: this._drawnEnd(chip, new Date(endAt)),
      // The shortest end the preview can show: min-minutes past the piece's
      // own top (a later day's piece starts at midnight, a clipped one at the
      // first visible hour), so the saved end matches the ghost.
      floorStart: this._pieceStart(chip, anchorStart),
      ghost,
      sourceChip: chip,
      eventId: chip.dataset.eventId,
      wall: this._wallContext(chip),
    }
  }

  // A Week grid's all-day bar: the ghost is the bar's REAL span shifted
  // by whole days, clipped to the visible columns, on its slot in the
  // all-day row — exactly the days the drop saves. Only the pointer is kept
  // to the visible columns, so a bar cut off at the week's edge moves either
  // way. Leaves _drag unset when the markup can't be dragged from.
  _startBar(mode, bar, startClientX) {
    const row = bar.closest("[data-all-day-row]")
    if (!row) return
    const cells = Array.from(row.querySelectorAll("[data-all-day-cell]"))
    const slot = Number(bar.dataset.slot)
    const firstDate = cells.length ? cells[0].dataset.allDayCell : null
    if (!firstDate || !Number.isInteger(slot)) return
    // The zone its days are midnights in (all-day: data-time-zone) or that
    // its days and wall-clock moves are in (timed: the grid's, which the grid
    // must give): missing or one this browser can't read leaves the bar
    // undraggable.
    const zone = bar.dataset.allDay === "true" ? bar.dataset.timeZone : this.timeZoneValue
    if (!supportedZone(zone)) return
    const span = this._barSpan(bar, firstDate)
    if (!span) return
    this._drag = {
      mode,
      cells,
      slot,
      // The bar's real first / last day, in days from the first column
      // (negative before it, past the last column after it).
      first: span[0],
      last: span[1],
      pressCol: this._colAt(cells, startClientX),
      delta: 0,
      ghost: this._buildBarGhost(row, slot),
      sourceChip: bar,
      eventId: bar.dataset.eventId,
      // How far back (in days, <= 0) a resize may pull the last day.
      minLastDelta: mode === "bar-resize" ? this._barMinLastDelta(bar) : -Infinity,
    }
    this._placeBarGhost(this._drag.ghost, span[0], span[1])
  }

  // [first, last] day of the bar, in days from `firstDate` (the first
  // column): the days the grid drew it on (data-start-date / data-end-date,
  // end inclusive — for a timed bar its wall days in the grid's zone). null
  // when unreadable.
  _barSpan(bar, firstDate) {
    const { startDate, endDate } = bar.dataset
    if (!isIsoDate(startDate) || !isIsoDate(endDate)) return null
    if (bar.dataset.allDay !== "true") {
      const startAt = new Date(bar.dataset.startAt || "")
      const endAt = new Date(bar.dataset.endAt || "")
      if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) return null
    }
    return [daysBetween(firstDate, startDate), daysBetween(firstDate, endDate)]
  }

  // A resize keeps an all-day bar on its first day, and a timed 24 h+ one
  // at least a wall-clock day long (it stays in the all-day row): its last
  // day can come back to the last day of start + 1 day, no further.
  _barMinLastDelta(bar) {
    const { startDate, endDate } = bar.dataset
    if (bar.dataset.allDay === "true") return daysBetween(endDate, startDate)
    const tz = this.timeZoneValue
    const minEnd = shiftWallDays(new Date(bar.dataset.startAt), 1, tz)
    return Math.min(0, daysBetween(endDate, lastWallDay(minEnd, tz)))
  }

  // After a drag-move/resize, swallow the click the browser fires next so the
  // chip's <a> href doesn't also navigate. Self-cleans if no click arrives.
  _suppressNextClick(chip) {
    const suppress = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }
    chip.addEventListener("click", suppress, { capture: true, once: true })
    setTimeout(() => {
      chip.removeEventListener("click", suppress, { capture: true })
    }, 350)
  }

  // ── Ghost rendering ───────────────────────────────────────────────────────

  // offsetLeft/offsetTop are relative to the element's offsetParent (the
  // position:relative day column), so the ghost MUST be appended to that same
  // parent — appending it to the grid root made it land in the wrong column.
  _ghostParent(el) {
    return el.offsetParent instanceof HTMLElement
      ? el.offsetParent
      : this.element
  }

  _buildGhost(cell) {
    const ghost = document.createElement("div")
    ghost.dataset.calendarGhost = "true"
    ghost.style.position = "absolute"
    ghost.style.left = cell.offsetLeft + "px"
    ghost.style.width = cell.offsetWidth + "px"
    ghost.style.top = cell.offsetTop + "px"
    ghost.style.height = this._minutesToPx(this.minMinutesValue) + "px"
    ghost.style.background = "rgba(59, 191, 198, 0.35)"
    ghost.style.border = "1px dashed rgba(59, 191, 198, 0.8)"
    ghost.style.pointerEvents = "none"
    // Above the now-line (z-20), under the sticky header block (z-30). The
    // grid ranks them in one stacking context: a z-index on the day column
    // would trap the ghost inside it.
    ghost.style.zIndex = "25"
    this._ghostParent(cell).appendChild(ghost)
    return ghost
  }

  _buildBarGhost(row, slot) {
    const ghost = document.createElement("div")
    ghost.dataset.calendarGhost = "true"
    ghost.style.background = "rgba(59, 191, 198, 0.35)"
    ghost.style.border = "1px dashed rgba(59, 191, 198, 0.8)"
    ghost.style.pointerEvents = "none"
    ghost.style.zIndex = "20"
    ghost.style.gridRow = String(slot + 1)
    row.appendChild(ghost)
    return ghost
  }

  // A span of days (from the first column) drawn clipped to the visible
  // columns. Column 1 is the row's gutter (WeekGridComponent#all_day_bar_style).
  _placeBarGhost(ghost, first, last) {
    const lastCol = this._drag ? this._drag.cells.length - 1 : last
    const from = Math.max(first, 0)
    const to = Math.min(last, lastCol)
    ghost.style.gridColumn = `${from + 2} / ${to + 3}`
  }

  // The visible day column under clientX, clamped to the first / last one:
  // a bar never drags beyond the days the grid shows.
  // From the cells' own rects, whatever their visual order: in a dir="rtl"
  // grid the first column is on the right, and a drag toward the next day
  // runs leftwards. The cell containing clientX, else the nearest one.
  _colAt(cells, clientX) {
    let best = 0
    let bestDistance = Infinity
    cells.forEach((cell, i) => {
      const { left, right } = cell.getBoundingClientRect()
      const distance = clientX < left ? left - clientX : clientX >= right ? clientX - right + 1 : 0
      if (distance < bestDistance) {
        best = i
        bestDistance = distance
      }
    })
    return best
  }

  // The day shift under the pointer — the pointer kept to the visible
  // columns — and the ghost drawn from the same shift the drop saves. A
  // resize moves the last day no further back than its floor
  // (#_barMinLastDelta).
  _updateBarGhost(clientX) {
    const d = this._drag
    if (!d || !d.ghost) return
    const raw = this._colAt(d.cells, clientX) - d.pressCol
    if (d.mode === "bar-resize") {
      d.delta = Math.max(raw, d.minLastDelta)
      this._placeBarGhost(d.ghost, d.first, d.last + d.delta)
    } else {
      d.delta = raw
      this._placeBarGhost(d.ghost, d.first + d.delta, d.last + d.delta)
    }
  }

  _cloneGhostFromChip(chip) {
    const ghost = document.createElement("div")
    ghost.dataset.calendarGhost = "true"
    ghost.style.position = "absolute"
    ghost.style.left = chip.offsetLeft + "px"
    ghost.style.width = chip.offsetWidth + "px"
    ghost.style.top = chip.offsetTop + "px"
    ghost.style.height = chip.offsetHeight + "px"
    ghost.style.background = "rgba(59, 191, 198, 0.35)"
    ghost.style.border = "1px dashed rgba(59, 191, 198, 0.8)"
    ghost.style.pointerEvents = "none"
    ghost.style.zIndex = "25"
    this._ghostParent(chip).appendChild(ghost)
    return ghost
  }

  _updateGhost(dy) {
    const d = this._drag
    if (!d || !d.ghost) return
    if (d.mode === "create" && d.wall) {
      const end = this._createEnd(d, dy)
      d.ghost.style.height =
        this._wallHeight(end, this._wallPx(d.anchorStart, d.wall), d.anchorStart, d.wall) + "px"
    } else if (d.mode === "create") {
      const minutes = Math.max(
        this.minMinutesValue,
        snapMinutes(this._pxToMinutes(dy), this.snapMinutesValue),
      )
      d.ghost.style.height = this._minutesToPx(minutes) + "px"
    } else if (d.mode === "move" && d.wall) {
      // Where the chip will be drawn: the new start's wall clock, as tall as
      // the entry is drawn from there.
      const [start] = this._moveResult(d, dy)
      const top = this._wallPx(start, d.wall)
      const drawnEnd = new Date(start.getTime() + (d.drawnEnd - d.anchorStart))
      const height = Math.max(this.chipMinPxValue, this._wallHeight(drawnEnd, top, start, d.wall))
      this._placeGhost(d.ghost, top, height)
    } else if (d.mode === "move") {
      const minutes = snapMinutes(this._pxToMinutes(dy), this.snapMinutesValue)
      const baseTop =
        d.baseTop ?? (d.sourceChip ? d.sourceChip.offsetTop : 0)
      const top = baseTop + this._minutesToPx(minutes)
      if (d.baseHeight === undefined) {
        d.ghost.style.top = top + "px"
      } else {
        this._placeGhost(d.ghost, top, d.baseHeight)
      }
    } else if (d.mode === "resize") {
      // From times, exactly as _finalize saves it: the new end, floored by
      // #_resizeMinEnd, measured from the piece's top — drawn at least
      // chipMinPx tall and kept inside the column, as the grid draws the
      // chip it will become (LaneLayout#visible_box).
      const newEnd = this._resizeEnd(d, dy)
      const from = d.floorStart ?? d.anchorStart
      const pieceTop = d.pieceTop ?? 0
      const drawn = d.wall
        ? this._wallHeight(newEnd, pieceTop, from, d.wall)
        : this._minutesToPx((newEnd - from) / 60_000)
      const [top, height] = this._fitToColumn(
        pieceTop,
        Math.max(this.chipMinPxValue, drawn),
        d.ghost.parentElement ? d.ghost.parentElement.offsetHeight : 0,
      )
      d.ghost.style.top = top + "px"
      d.ghost.style.height = height + "px"
    }
  }

  _cleanupGhost() {
    const ghosts = this.element.querySelectorAll('[data-calendar-ghost="true"]')
    ghosts.forEach((g) => g.remove())
  }

  // ── Finalize ──────────────────────────────────────────────────────────────

  _finalize(dy) {
    const d = this._drag
    if (!d) return null

    const snapMin = this.snapMinutesValue
    const minMin = this.minMinutesValue

    if (d.mode === "create") {
      const start = d.anchorStart
      const end = this._createEnd(d, dy)
      return {
        action: "create",
        start_at: start.toISOString(),
        end_at: end.toISOString(),
      }
    }

    if (d.mode === "move") {
      const deltaMin = snapMinutes(this._pxToMinutes(dy), snapMin)
      // No-op if no movement (don't spam the server).
      if (deltaMin === 0) return null
      const [newStart, newEnd] = this._moveResult(d, dy)
      return {
        action: "move",
        id: d.eventId,
        start_at: newStart.toISOString(),
        end_at: newEnd.toISOString(),
      }
    }

    if (d.mode === "resize") {
      const newEnd = this._resizeEnd(d, dy)
      if (newEnd.getTime() === d.anchorEnd.getTime()) return null
      return {
        action: "resize",
        id: d.eventId,
        start_at: d.anchorStart.toISOString(),
        end_at: newEnd.toISOString(),
      }
    }

    return null
  }

  // A bar's new days → its change, or null when it lands where it was.
  _finalizeBar(clientX) {
    const d = this._drag
    if (!d) return null
    this._updateBarGhost(clientX)
    const delta = d.delta
    if (delta === 0) return null
    const bar = d.sourceChip
    const move = d.mode === "bar-move"
    const allDay = bar.dataset.allDay === "true"
    let start
    let end
    if (allDay) {
      const { startDate, endDate, timeZone } = bar.dataset
      if (!startDate || !endDate) return null
      const firstDay = addDays(startDate, move ? delta : 0)
      let lastDay = addDays(endDate, delta)
      if (lastDay < firstDay) lastDay = firstDay
      if (!move && lastDay === endDate) return null
      // Midnight of the first day, and of the day after the last (exclusive).
      start = zonedWallToInstant(dateWall(firstDay), timeZone)
      end = zonedWallToInstant(dateWall(addDays(lastDay, 1)), timeZone)
    } else {
      const startAt = new Date(bar.dataset.startAt || "")
      const endAt = new Date(bar.dataset.endAt || "")
      if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) return null
      const tz = this.timeZoneValue
      start = move ? shiftWallDays(startAt, delta, tz) : startAt
      end = shiftWallDays(endAt, delta, tz)
      // A resize keeps at least a day: it stays in the all-day row.
      const minEnd = shiftWallDays(startAt, 1, tz)
      if (!move && end < minEnd) end = minEnd
      // Never past the ghost's last day (a wall time pushed through a DST
      // gap could spill into the next): at most the end of that day.
      const lastDay = addDays(bar.dataset.endDate, delta)
      if (lastWallDay(end, tz) > lastDay) end = zonedWallToInstant(dateWall(addDays(lastDay, 1)), tz)
    }
    return {
      action: move ? "move" : "resize",
      id: d.eventId,
      start_at: start.toISOString(),
      end_at: end.toISOString(),
      all_day: allDay,
    }
  }

  // The times a move saves (and its ghost shows): the start shifted by the
  // snapped drag — on the wall clock when the grid gives its zone — and the
  // end the same elapsed time after it.
  _moveResult(d, dy) {
    const deltaMin = snapMinutes(this._pxToMinutes(dy), this.snapMinutesValue)
    const newStart = d.wall
      ? shiftWallMinutes(d.anchorStart, deltaMin, d.wall.timeZone)
      : new Date(d.anchorStart.getTime() + deltaMin * 60_000)
    return [newStart, new Date(newStart.getTime() + (d.anchorEnd - d.anchorStart))]
  }

  // The end a resize saves (and its ghost shows), floored by #_resizeMinEnd.
  _resizeEnd(d, dy) {
    const deltaMin = snapMinutes(this._pxToMinutes(dy), this.snapMinutesValue)
    if (deltaMin === 0) return d.anchorEnd
    const end = d.wall
      ? shiftWallMinutes(d.anchorEnd, deltaMin, d.wall.timeZone)
      : new Date(d.anchorEnd.getTime() + deltaMin * 60_000)
    const minEnd = this._resizeMinEnd(d)
    return end < minEnd ? minEnd : end
  }

  // The end a create drag saves (and its ghost shows): at least min-minutes.
  _createEnd(d, dy) {
    const dragMin = Math.max(
      this.minMinutesValue,
      snapMinutes(this._pxToMinutes(dy), this.snapMinutesValue),
    )
    return d.wall
      ? shiftWallMinutes(d.anchorStart, dragMin, d.wall.timeZone)
      : new Date(d.anchorStart.getTime() + dragMin * 60_000)
  }

  _emitChange(detail) {
    this.element.dispatchEvent(
      new CustomEvent("ui-calendar:event-changed", {
        detail,
        bubbles: true,
      }),
    )
  }

  // ── Unit conversions ──────────────────────────────────────────────────────

  _pxToMinutes(px) {
    return (px * 60) / this.hourHeightValue
  }

  // The shortest end a resize saves — and its ghost shows: min-minutes past
  // the drawn piece's top, never past the end it is drawn to (a resize never
  // lengthens an entry already shorter than that). A zero-length entry is
  // drawn an hour long, so it can be pulled up to min-minutes too.
  _resizeMinEnd(d) {
    const minMs = this.minMinutesValue * 60_000
    const floor = d.floorStart ?? d.anchorStart
    return new Date(
      Math.min(d.anchorEnd.getTime(), floor.getTime() + minMs),
    )
  }

  // Sets a ghost's top / height kept inside its column (#_fitToColumn).
  _placeGhost(ghost, top, height) {
    const [fitTop, fitHeight] = this._fitToColumn(
      top,
      height,
      ghost.parentElement ? ghost.parentElement.offsetHeight : 0,
    )
    ghost.style.top = fitTop + "px"
    ghost.style.height = fitHeight + "px"
  }

  // A box kept inside its column, as Ui::Calendar::LaneLayout#visible_box
  // keeps a chip: cut at the bottom, at least chipMinPx tall, moved up just
  // enough to fit. An unknown (0) column height leaves it as is.
  _fitToColumn(top, height, columnPx) {
    if (columnPx <= 0 || (top >= 0 && top + height <= columnPx)) {
      return [top, height]
    }
    const visibleTop = Math.min(Math.max(top, 0), columnPx)
    const bottom = Math.min(Math.max(top + height, 0), columnPx)
    const shown = Math.max(bottom - visibleTop, Math.min(this.chipMinPxValue, columnPx))
    return [Math.min(visibleTop, columnPx - shown), shown]
  }

  // The end the chip is drawn to (data-drawn-end-at), or `fallback` when the
  // markup has none or it is unreadable.
  _drawnEnd(chip, fallback) {
    const raw = chip.dataset.drawnEndAt
    const drawn = raw ? new Date(raw) : null
    return drawn && !Number.isNaN(drawn.getTime()) ? drawn : fallback
  }

  // A px number the grid rendered on the chip (data-piece-top-px …), or
  // null when absent (an older host's markup).
  _pxData(chip, key) {
    const raw = chip.dataset[key]
    if (raw === undefined || raw === "") return null
    const value = Number(raw)
    return Number.isFinite(value) ? value : null
  }

  // Where the chip's drawn piece starts (data-piece-start-at): later than
  // the entry's real start when the grid clipped it at midnight or at the
  // first visible hour. Never earlier than the real start.
  _pieceStart(chip, anchorStart) {
    const raw = chip.dataset.pieceStartAt
    const pieceStart = raw ? new Date(raw) : anchorStart
    return Number.isNaN(pieceStart.getTime()) || pieceStart < anchorStart
      ? anchorStart
      : pieceStart
  }

  _minutesToPx(min) {
    return (min * this.hourHeightValue) / 60
  }

  // The grid's zone and the column's date, when the grid gives its zone
  // (else null: elapsed-time math, as older markup expects).
  _wallContext(el) {
    const timeZone = this.timeZoneValue
    const column = el.closest("[data-day-column]")
    const columnDate = column ? column.dataset.dayColumn : null
    // A zone this browser's Intl doesn't know: elapsed-time math instead.
    return timeZone && columnDate && supportedZone(timeZone) ? { timeZone, columnDate } : null
  }

  // Where the grid draws `instant` in its column: Ui::Calendar::DayColumn's
  // wall-clock rule (minutes from the first visible hour of the column's
  // date, on the wall clock; a later date adds whole days).
  _wallPx(instant, wall) {
    const p = wallParts(instant, wall.timeZone)
    const days = daysBetween(wall.columnDate, isoDate(p))
    const minutes = days * 1440 + p.h * 60 + p.mi - this.firstHourValue * 60
    return Math.round(this._minutesToPx(minutes))
  }

  // A box from `top` to where `to` is drawn; inside a fall-back day's
  // repeated hour the wall clock runs backwards, so it is measured by its
  // real length from `measureFrom` (as DayColumn does).
  _wallHeight(to, top, measureFrom, wall) {
    const height = this._wallPx(to, wall) - top
    return height > 0 ? height : this._minutesToPx((to - measureFrom) / 60_000)
  }
}

/**
 * Pure helper — snap an arbitrary minutes value to the nearest `step` minutes.
 * Negative values snap symmetrically. Exported for unit tests.
 */
export function snapMinutes(minutes, step) {
  if (!step || step <= 0) return Math.round(minutes)
  return Math.round(minutes / step) * step
}

/**
 * Pure helper — snap a Date to the nearest `step` minutes.
 * Useful for tests and for the page-level controller.
 */
export function snapDateToMinutes(date, step) {
  const ms = date.getTime()
  const stepMs = step * 60_000
  return new Date(Math.round(ms / stepMs) * stepMs)
}

// ── Wall-clock helpers (CONEK-8153) ─────────────────────────────────────────
// Pure, exported for unit tests. A "wall" is { y, mo, d, h, mi, s } in an IANA
// zone; an empty zone means UTC.

const wallFormats = new Map()
const zoneSupport = new Map()

/** Whether this browser's Intl knows `timeZone` (checked once per zone). */
export function supportedZone(timeZone) {
  if (!timeZone) return false
  if (!zoneSupport.has(timeZone)) {
    let ok = true
    try {
      new Intl.DateTimeFormat("en-US", { timeZone })
    } catch {
      ok = false
    }
    zoneSupport.set(timeZone, ok)
  }
  return zoneSupport.get(timeZone)
}

/** The wall clock of `date` in `timeZone`. */
export function wallParts(date, timeZone) {
  const zone = timeZone || "UTC"
  let format = wallFormats.get(zone)
  if (!format) {
    format = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
    wallFormats.set(zone, format)
  }
  const parts = {}
  for (const { type, value } of format.formatToParts(date)) parts[type] = Number(value)
  return { y: parts.year, mo: parts.month, d: parts.day, h: parts.hour % 24, mi: parts.minute, s: parts.second }
}

// The zone's UTC offset (ms) at an instant.
function offsetAt(ms, timeZone) {
  const p = wallParts(new Date(ms), timeZone)
  const wholeSecond = Math.floor(ms / 1000) * 1000
  return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - wholeSecond
}

/**
 * The instant a wall clock names in `timeZone`, as ActiveSupport's
 * TimeZone#local reads it: a time in the spring-forward gap moves forward
 * by the gap (Madrid 2027-03-28 02:00 → 03:00+02:00); a repeated fall-back
 * time takes its first occurrence (2027-10-31 02:00 → 02:00+02:00).
 */
export function zonedWallToInstant({ y, mo, d, h = 0, mi = 0, s = 0 }, timeZone) {
  const asUtc = Date.UTC(y, mo - 1, d, h, mi, s)
  const before = offsetAt(asUtc - 12 * 3_600_000, timeZone)
  const after = offsetAt(asUtc + 12 * 3_600_000, timeZone)
  const early = asUtc - before
  const late = asUtc - after
  const earlyOk = offsetAt(early, timeZone) === before
  const lateOk = offsetAt(late, timeZone) === after
  if (earlyOk && lateOk) return new Date(Math.min(early, late))
  if (lateOk) return new Date(late)
  // Valid in the earlier offset, or in the gap: read it in the offset before
  // the change, which lands just after the gap.
  return new Date(early)
}

/** An ISO date ("YYYY-MM-DD") `n` days later (earlier when negative). */
export function addDays(isoDateValue, n) {
  const [y, mo, d] = isoDateValue.split("-").map(Number)
  return new Date(Date.UTC(y, mo - 1, d + n)).toISOString().slice(0, 10)
}

/** `date` moved `minutes` on the wall clock of `timeZone` (seconds kept). */
export function shiftWallMinutes(date, minutes, timeZone) {
  if (!timeZone) return new Date(date.getTime() + minutes * 60_000)
  const p = wallParts(date, timeZone)
  const moved = new Date(Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi + minutes, p.s))
  const instant = zonedWallToInstant(
    {
      y: moved.getUTCFullYear(),
      mo: moved.getUTCMonth() + 1,
      d: moved.getUTCDate(),
      h: moved.getUTCHours(),
      mi: moved.getUTCMinutes(),
      s: moved.getUTCSeconds(),
    },
    timeZone,
  )
  return new Date(instant.getTime() + (date.getTime() % 1000))
}

/** `date` moved `n` days on the wall clock of `timeZone`, its time of day kept. */
export function shiftWallDays(date, n, timeZone) {
  return shiftWallMinutes(date, n * 1440, timeZone)
}

// Midnight of an ISO date, as a wall.
function dateWall(isoDateValue) {
  const [y, mo, d] = isoDateValue.split("-").map(Number)
  return { y, mo, d }
}

function isoDate({ y, mo, d }) {
  return new Date(Date.UTC(y, mo - 1, d)).toISOString().slice(0, 10)
}

// The last day an end covers on the wall clock of `timeZone` (an end at
// midnight belongs to the day before, as EntrySpan reads it).
function lastWallDay(end, timeZone) {
  return isoDate(wallParts(new Date(end.getTime() - 1), timeZone))
}

// An ISO instant the markup gave that parses (not missing or "garbage").
function validInstant(value) {
  return !!value && !Number.isNaN(new Date(value).getTime())
}

function isIsoDate(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
}

// Whole days from one ISO date to another.
function daysBetween(fromIso, toIso) {
  return Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 86_400_000)
}
